import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Box, Flex, Text } from '@chakra-ui/react';
import { useDroppable } from '@dnd-kit/core'
import { DAY_START_MINUTES, SLOT_MINUTES, minutesToTime, slotLabels, timeToMinutes } from './lobbyScheduleUtils'
import { assignStickyColumns, cardGeometry, cardLayout, laneWidthPx } from './timelineLayout'
import { MeetingAgendaCard } from './MeetingAgendaCard'
import type { ScheduleLegislatorRowDto, ScheduleMeetingDto } from '../../../types/lobbyDay'

const SLOT_PX = 60
const TIME_COL_PX = 52
const COL_GAP_PX = 4
const PX_PER_MIN = SLOT_PX / SLOT_MINUTES

interface ScheduleAgendaProps {
  legislators: ScheduleLegislatorRowDto[]
  meetings: ScheduleMeetingDto[]
  date: string
  colorForMeeting: (m: ScheduleMeetingDto) => string
  conflictChips: Map<string, Set<string>>
  flashMeetingIds: Set<string>
  highlightAttendeeIds?: Set<string> | null
  highlightLegislatorIds?: Set<string> | null
  onOpenMeeting: (meetingId: string) => void
  onDeleteMeeting: (meetingId: string) => void
  onResizeMeeting: (meetingId: string, durationMin: number) => void
  onHoverAttendee?: (attendeeId: string | null) => void
  onHoverCard?: (legislatorId: string | null) => void
}

/** A full-lane-width invisible droppable behind the cards, one per slot — catches drops
 *  on empty time (drag-reschedule a card, or drop a pool attendee to create a meeting). */
function SlotDroppable({ slotTime, top }: { slotTime: string; top: number }) {
  const { setNodeRef, isOver } = useDroppable({ id: `slot:${slotTime}`, data: { type: 'slot', slotTime } })
  return (
    <Box
      ref={setNodeRef} position="absolute" left={0} right={0} top={`${top}px`} height={`${SLOT_PX}px`}
      borderTopWidth="1px" borderColor="gray.100" bg={isOver ? 'accent.soft' : undefined}
    />
  )
}

export function ScheduleAgenda({
  legislators, meetings, date, colorForMeeting, conflictChips, flashMeetingIds,
  highlightAttendeeIds, highlightLegislatorIds, onOpenMeeting, onDeleteMeeting, onResizeMeeting,
  onHoverAttendee, onHoverCard,
}: ScheduleAgendaProps) {
  const labels = useMemo(() => slotLabels(), [])
  const canvasHeight = labels.length * SLOT_PX

  const dayMeetings = useMemo(() => meetings.filter((m) => m.meetingDate === date), [meetings, date])
  const legislatorById = useMemo(
    () => new Map(legislators.map((l) => [l.legislatorId, l] as const)), [legislators])

  // Sticky columns: keep each card's prior column order stable across edits; reset per day.
  const prevColsRef = useRef<{ date: string; cols: Map<string, number> }>({ date, cols: new Map() })
  if (prevColsRef.current.date !== date) prevColsRef.current = { date, cols: new Map() }
  const columns = useMemo(
    () => assignStickyColumns(dayMeetings, prevColsRef.current.cols), [dayMeetings, date])
  useEffect(() => {
    prevColsRef.current = { date, cols: new Map([...columns].map(([id, a]) => [id, a.colIndex])) }
  }, [columns, date])

  const maxCols = useMemo(() => {
    let mx = 1
    for (const c of columns.values()) mx = Math.max(mx, c.colCount)
    return mx
  }, [columns])

  // A callback ref (not useRef + useEffect([])) so the observer attaches the instant the lane
  // viewport mounts and detaches when it unmounts. The empty-roster branch below returns BEFORE
  // rendering the viewport, so on a 0→N in-place legislators refetch the node mounts later than
  // the component does; a [] -deps effect ran once (node still null → early return) and never
  // re-attached, pinning containerW at 0 and measuring every card's chip overflow against a
  // zero-width lane.
  const [containerW, setContainerW] = useState(0)
  const roRef = useRef<ResizeObserver | null>(null)
  const laneViewportRef = useCallback((el: HTMLDivElement | null) => {
    roRef.current?.disconnect()
    if (!el) { roRef.current = null; return }
    setContainerW(el.clientWidth)
    const ro = new ResizeObserver((entries) => {
      for (const e of entries) setContainerW(e.contentRect.width)
    })
    ro.observe(el)
    roRef.current = ro
  }, [])
  const laneW = laneWidthPx(maxCols, containerW)

  if (legislators.length === 0) {
    return (
      <Box flex={1} minW={0} borderWidth="1px" borderColor="gray.200" borderRadius="lg" bg="surface" py={8}>
        <Text textAlign="center" color="gray.500">
          No legislators on this event yet. Add them from the Recruit tab (Attendees → Recruit).
        </Text>
      </Box>
    )
  }

  return (
    <Box flex={1} minW={0} borderWidth="1px" borderColor="gray.200" borderRadius="lg" bg="surface" overflow="hidden">
      <Flex align="stretch">
        {/* time gutter */}
        <Box position="relative" width={`${TIME_COL_PX}px`} flexShrink={0} height={`${canvasHeight}px`}>
          {labels.map((label, i) => (
            <Text
              key={label} position="absolute" top={`${i * SLOT_PX}px`} right="6px"
              fontSize="0.65rem" color="gray.500" transform="translateY(-1px)"
            >
              {label}
            </Text>
          ))}
        </Box>

        {/* lane viewport — scrolls horizontally only when the lane exceeds it */}
        <Box ref={laneViewportRef} flex={1} minW={0} overflowX="auto" borderLeftWidth="1px" borderColor="gray.100">
          <Box position="relative" width={`${laneW}px`} height={`${canvasHeight}px`}>
            {labels.map((_, i) => (
              <SlotDroppable
                key={i} top={i * SLOT_PX}
                slotTime={minutesToTime(DAY_START_MINUTES + i * SLOT_MINUTES)}
              />
            ))}
            {dayMeetings.map((m) => {
              const col = columns.get(m.id) ?? { colIndex: 0, colCount: 1 }
              const colWidthPx = laneW / col.colCount
              const widthPx = colWidthPx - COL_GAP_PX
              const geo = cardGeometry(m, PX_PER_MIN)
              const durationMin = timeToMinutes(m.endTime) - timeToMinutes(m.startTime)
              return (
                <MeetingAgendaCard
                  key={m.id} meeting={m} legislator={legislatorById.get(m.legislatorId)}
                  color={colorForMeeting(m)}
                  layout={cardLayout({ widthPx, heightPx: geo.height, durationMin })}
                  isConflicted={conflictChips.has(m.id)} isFlashing={flashMeetingIds.has(m.id)}
                  isHighlighted={!!highlightLegislatorIds?.has(m.legislatorId)}
                  conflictedAttendeeIds={conflictChips.get(m.id)} highlightAttendeeIds={highlightAttendeeIds}
                  top={geo.top} height={geo.height} left={`${col.colIndex * colWidthPx}px`} width={`${widthPx}px`}
                  onOpen={onOpenMeeting} onDelete={onDeleteMeeting} onResize={onResizeMeeting}
                  onHoverAttendee={onHoverAttendee} onHoverCard={onHoverCard}
                />
              )
            })}
          </Box>
        </Box>
      </Flex>
    </Box>
  )
}
